/**
 * commentUi
 *
 * Localized strings for comment-related UX (composer feedback, thread
 * actions, etc.). Mirrors the existing CRA i18n adapter pattern (see
 * authUi.ts, taskUi.ts).
 *
 * The keys exposed via `commentNotifyResolver` are the canonical
 * notify keys for the comment domain: `comment.added`,
 * `comment.addFailed`, etc. Register the resolver once at app root
 * (e.g. in `frontend/src/index.tsx` or a `BootstrapNotify` component)
 * to enable `notify.success({ key: "comment.added" })`.
 */

export type CommentLocale = "en-US" | "ja-JP" | "ko-KR";

export interface CommentUiStrings {
  /** Toast on successful root/reply comment add. */
  added: string;
  /** Toast on failed comment add. */
  addFailed: string;
  /** Toast on successful comment edit. */
  edited: string;
  /** Toast on failed comment edit. */
  editFailed: string;
  /** Toast on successful comment delete. */
  deleted: string;
  /** Toast on failed comment delete. */
  deleteFailed: string;
  /** Empty-content composer validation error. */
  emptyContent: string;
}

const STRINGS: Record<CommentLocale, CommentUiStrings> = {
  "en-US": {
    added: "Comment added",
    addFailed: "Failed to add comment",
    edited: "Comment updated",
    editFailed: "Failed to update comment",
    deleted: "Comment deleted",
    deleteFailed: "Failed to delete comment",
    emptyContent: "Comment cannot be empty",
  },
  "ja-JP": {
    added: "コメントを追加しました",
    addFailed: "コメントを追加できませんでした",
    edited: "コメントを更新しました",
    editFailed: "コメントを更新できませんでした",
    deleted: "コメントを削除しました",
    deleteFailed: "コメントを削除できませんでした",
    emptyContent: "コメントを入力してください",
  },
  "ko-KR": {
    added: "댓글이 추가되었습니다",
    addFailed: "댓글을 추가하지 못했습니다",
    edited: "댓글이 업데이트되었습니다",
    editFailed: "댓글을 업데이트하지 못했습니다",
    deleted: "댓글이 삭제되었습니다",
    deleteFailed: "댓글을 삭제하지 못했습니다",
    emptyContent: "댓글을 입력해 주세요",
  },
};

export function resolveCommentLocale(): CommentLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getCommentUiStrings(locale?: CommentLocale): CommentUiStrings {
  const key = locale ?? resolveCommentLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}

/**
 * Notify resolver for the `comment.*` keyspace. Pass to
 * `registerNotifyResolver` once at app boot to enable
 * `notify.success({ key: "comment.added" })` etc. across the app.
 */
export function commentNotifyResolver(key: string): string | null {
  if (!key.startsWith("comment.")) {
    return null;
  }
  const subkey = key.slice("comment.".length) as keyof CommentUiStrings;
  const strings = getCommentUiStrings();
  return strings[subkey] ?? null;
}
